import type {Schema} from './api';
import type {PageRegion} from './PdfSource';

export type MarkerPrecision='exact'|'approximate'|'unresolved';
export type RegionMatch={region?:PageRegion;precision:MarkerPrecision};
export type CadRecord={key:string;x:number;y:number;height?:number;text:string;bounds?:number[]};

/**
 * Convert an extracted record into a viewport region while retaining the
 * provenance of the location. Only extractor-provided bounds are exact;
 * text-anchor boxes are navigation hints and must be shown as approximate.
 */
export function regionForRecord(record:CadRecord|undefined,page:Schema['PublicationPage']|undefined,sourceSheet:string):RegionMatch {
  if(!record || !page || page.sheet!==sourceSheet) return {precision:'unresolved'};
  const [left,bottom,right,top]=page.bbox;
  const width=right-left;
  const height=top-bottom;
  if(!(width>0&&height>0) || ![record.x,record.y].every(Number.isFinite)) return {precision:'unresolved'};
  if(record.x<left || record.x>right || record.y<bottom || record.y>top) return {precision:'unresolved'};
  const clamp=(value:number)=>Math.max(0,Math.min(1,value));
  // The extractor writes a block it could not read as a placeholder at 0,0 without a text height
  // (extract.py, «Блок требует визуальной проверки»). That is not a position: no marker.
  if(record.x===0 && record.y===0 && !record.height && !record.bounds) return {precision:'unresolved'};
  const exact=record.bounds;
  if(exact?.length===4 && exact.every(Number.isFinite) && exact[2]>exact[0] && exact[3]>exact[1]) {
    const x=clamp((exact[0]-left)/width);
    const y=clamp((top-exact[3])/height);
    const x2=clamp((exact[2]-left)/width);
    const y2=clamp((top-exact[1])/height);
    if(x2>x && y2>y) return {precision:'exact',region:{x,y,width:x2-x,height:y2-y}};
  }
  const textHeight=record.height || page.text_height || 1;
  const x=clamp((record.x-left)/width-.008);
  const y=clamp((top-record.y-textHeight*1.6)/height);
  const inferredWidth=Math.min(.3,Math.max(.02,record.text.length*textHeight*.7/width));
  const inferredHeight=Math.max(.012,textHeight*2.4/height);
  const region={x,y,width:Math.min(1-x,inferredWidth),height:Math.min(1-y,inferredHeight)};
  return region.width>0&&region.height>0 ? {precision:'approximate',region} : {precision:'unresolved'};
}
